import { canAccessGmailIntegration } from "../../utils/gmailScope";
import { canAccessSecurityConsole } from "../../utils/securityScope";
import { isAdminRole, normalizeRole } from "./ensureRouteAccess";

/** Settings submenu items that must appear even when an older saved settingsConfig omits them. */
export const REQUIRED_SETTINGS_ITEMS = [
  {
    SubmenuKey: "sub1",
    item: {
      key: "2b",
      auth: true,
      to: "kompania/timezone/ekipeve",
      title: "Timezone e ekipeve",
    },
    roles: ["admin", "super admin"],
  },
  {
    SubmenuKey: "sub2",
    item: {
      key: "4b",
      auth: true,
      to: "sistemi/rotacioni",
      title: "Rotacioni i turneve",
    },
    roles: ["admin", "super admin"],
  },
  {
    SubmenuKey: "subIntegrime",
    createSectionIfMissing: {
      title: "Integrime",
      SubmenuKey: "subIntegrime",
      auth: true,
      submenu: [],
    },
    item: {
      key: 30,
      auth: true,
      to: "integrime/gmail",
      title: "Gmail Inbox",
    },
    // Email allowlist only — Admin role is not enough.
    gmailAllowlistOnly: true,
  },
  {
    SubmenuKey: "subSecurity",
    createSectionIfMissing: {
      title: "Siguria",
      SubmenuKey: "subSecurity",
      auth: true,
      submenu: [],
    },
    item: {
      key: "sec-sessions",
      auth: true,
      to: "siguria/sesionet",
      title: "Menaxheri i sesioneve",
    },
    securityAllowlistOnly: true,
  },
];

const ADMIN_ONLY_SETTINGS_TOS = [
  "kompania/timezone/ekipeve",
  "sistemi/rotacioni",
];

/**
 * Merge required settings submenu entries into a user's settingsConfig.
 * Creates missing parent sections (e.g. Integrime) when needed.
 * Strips Gmail/Integrime for anyone not on the hard email allowlist.
 *
 * @param {Array} settingsConfig
 * @param {string} userRole
 * @param {object|string} [authOrEmail]
 */
export function ensureSettingsAccess(
  settingsConfig = [],
  userRole,
  authOrEmail
) {
  let config = Array.isArray(settingsConfig)
    ? settingsConfig.map((section) => ({
        ...section,
        submenu: Array.isArray(section?.submenu)
          ? section.submenu.map((row) => ({ ...row }))
          : section?.submenu,
      }))
    : [];
  const role = normalizeRole(userRole);
  const admin = isAdminRole(role);
  const authCtx =
    typeof authOrEmail === "string" ? { email: authOrEmail } : authOrEmail;
  const gmailOk = canAccessGmailIntegration(authCtx);
  const securityOk = canAccessSecurityConsole(authCtx);

  if (!gmailOk) {
    config = config
      .filter((section) => section?.SubmenuKey !== "subIntegrime")
      .map((section) => ({
        ...section,
        submenu: Array.isArray(section?.submenu)
          ? section.submenu.filter(
              (row) => !String(row?.to || "").includes("integrime/gmail")
            )
          : section?.submenu,
      }));
  }
  if (!securityOk) {
    config = config
      .filter((section) => section?.SubmenuKey !== "subSecurity")
      .map((section) => ({
        ...section,
        submenu: Array.isArray(section?.submenu)
          ? section.submenu.filter(
              (row) => !String(row?.to || "").includes("siguria/sesionet")
            )
          : section?.submenu,
      }));
  }

  // Non-admins: strip admin-only settings even if saved accessConfig has auth:true.
  if (!admin) {
    config = config.map((section) => ({
      ...section,
      submenu: Array.isArray(section?.submenu)
        ? section.submenu.filter(
            (row) => !ADMIN_ONLY_SETTINGS_TOS.includes(String(row?.to || ""))
          )
        : section?.submenu,
    }));
  }

  REQUIRED_SETTINGS_ITEMS.forEach((req) => {
    if (req.gmailAllowlistOnly) {
      if (!gmailOk) return;
    } else if (req.securityAllowlistOnly) {
      if (!securityOk) return;
    } else {
      // Role list is authoritative — do not infer admin from settings sections.
      const roleOk = !req.roles?.length || req.roles.includes(role);
      if (!roleOk) return;
    }

    let sectionIdx = config.findIndex((s) => s?.SubmenuKey === req.SubmenuKey);
    if (sectionIdx < 0) {
      if (!req.createSectionIfMissing) return;
      config.push({
        ...req.createSectionIfMissing,
        submenu: [...(req.createSectionIfMissing.submenu || [])],
      });
      sectionIdx = config.length - 1;
    }

    const section = config[sectionIdx];
    const submenu = Array.isArray(section.submenu) ? [...section.submenu] : [];
    const existingIdx = submenu.findIndex((row) => row?.to === req.item.to);

    if (existingIdx >= 0) {
      submenu[existingIdx] = {
        ...submenu[existingIdx],
        ...req.item,
        auth: true,
      };
    } else {
      submenu.push({ ...req.item });
    }

    config[sectionIdx] = {
      ...section,
      auth: true,
      submenu,
    };
  });

  return config;
}
